import { rem, TmElement } from '@thiememeulenhoff/lit-sdk';
import { html, PropertyValues } from 'lit';
import {
  customElement,
  property,
  queryAssignedElements,
  queryAssignedNodes,
} from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { tagName } from '../../../helpers';
import { TocActionComponent } from '../toc-action/toc-action.component';
import { styles } from './toc-item.styles';

const tag = tagName('toc-item');

@customElement(tag)
export class TocItemComponent extends TmElement {
  static override styles = styles;

  @queryAssignedElements({ selector: 'a-toc-item', slot: 'children' })
  tocItems!: Array<this>;

  @queryAssignedElements({ selector: 'a-toc-action', slot: 'actions' })
  tocActions!: Array<TocActionComponent>;

  @queryAssignedElements({ selector: '*:not(a-toc-item):not(a-toc-action)' })
  tocOther!: Array<HTMLElement>;

  @queryAssignedNodes()
  tocNodes!: Array<Node>;

  @property({ type: Boolean, reflect: true })
  secondary = false;

  @property({ type: Boolean })
  open = false;

  @property({ type: String })
  href?: string;

  @property({ type: String })
  target?: '_blank' | '_self' | '_parent' | '_top';

  @property({ type: String })
  marker?: string;

  @property({ type: Boolean, reflect: true })
  active = false;

  @property({ attribute: 'scroll-into-view', type: Boolean, reflect: true })
  scrollItemIntoView = false;

  @property({ type: Number })
  nestingLevel = 0;

  @property({ type: Boolean })
  disabled = false;

  @property({ type: Boolean })
  disablePadding = false;

  #isNestedItem() {
    return this.parentElement instanceof TocItemComponent;
  }

  #renderChildrenSlot() {
    return html`<slot name="children" @slotchange=${this.onSlotChange}></slot>`;
  }

  #renderActionsSlot() {
    return html`<slot name="actions" @slotchange=${this.onSlotChange}></slot>`;
  }

  #renderTocWithChildren() {
    return html`<a-accordion
      ?open=${this.open}
      class=${classMap({
        'toc-item--has-actions': !!(this.tocActions.length > 0 || this.href),
      })}
    >
      <div slot="summary">${this.#renderItemSummary()}</div>
      ${this.#renderChildrenSlot()}
    </a-accordion>`;
  }

  #renderTocWithoutChildren() {
    return html`<div
        part="wrapper"
        class=${classMap({
          'toc-item-wrapper': true,
          'toc-item--has-actions': this.tocActions.length,
        })}
      >
        ${this.#renderItemSummary()}
      </div>
      ${this.#renderChildrenSlot()}`;
  }

  #renderItemSummary() {
    return this.href
      ? html`<a-toc-action
            ?disabled=${this.disabled}
            href=${this.href}
            target=${this.target}
            >${this.#slottedTitleRender(false)}</a-toc-action
          >${this.#renderActionsSlot()}`
      : this.#slottedTitleRender();
  }

  #slottedTitleRender(withActionSlot = true) {
    const actionSlot = withActionSlot ? this.#renderActionsSlot() : '';

    if (this.marker) {
      return html`<div class="marker-container">
        <a-marker class="marker">${this.marker}</a-marker>
        <slot @slotchange=${this.onSlotChange}></slot>
        ${actionSlot}
      </div>`;
    }

    return html`<slot @slotchange=${this.onSlotChange}></slot>${actionSlot}`;
  }

  override render() {
    if (this.tocItems.length) {
      return this.#renderTocWithChildren();
    }

    return this.#renderTocWithoutChildren();
  }

  onSlotChange() {
    // // Force update when slot changes
    if (this.tocItems.length || this.tocActions.length) {
      this.requestUpdate();
    }
  }

  #computePadding(): string {
    return rem(12, 12, 12, 12 + this.nestingLevel * 20).toString();
  }

  #calculateNestingLevel(): number {
    let level = 0;
    let parent = this.parentElement;

    while (parent instanceof TocItemComponent) {
      level += 1;
      parent = parent.parentElement;
    }

    return level;
  }

  override connectedCallback() {
    super.connectedCallback();

    if (this.#isNestedItem()) {
      this.slot = 'children';
    }

    this.#openIfActive();
  }

  protected override firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);

    // All properties are guaranteed to be set correctly at this point
    this.#applyPaddingLogic();
  }

  #applyPaddingLogic() {
    if (!this.disablePadding) {
      this.#setNestingPadding();
    } else {
      // When padding is disabled, reset the toc action button padding to 0
      this.style.removeProperty('--toc-action-button-padding');
    }
  }

  protected override updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);

    // Re-apply padding logic when disablePadding changes
    if (_changedProperties.has('disablePadding')) {
      this.#applyPaddingLogic();
    }

    if (
      _changedProperties.has('active') &&
      this.scrollItemIntoView &&
      this.active
    ) {
      this.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  #setNestingPadding() {
    this.nestingLevel = this.#calculateNestingLevel();
    const computedPadding = this.#computePadding();

    if (this.nestingLevel !== 0) {
      this.style.setProperty('--tm-theme-a-accordion-padding', computedPadding);

      this.style.setProperty('--toc-item-wrapper-padding', computedPadding);
    }

    this.style.setProperty('--toc-action-button-padding', computedPadding);
  }

  #setOpen() {
    this.open = true;

    if (this.parentElement instanceof TocItemComponent) {
      this.parentElement.#setOpen();
    }
  }

  #openIfActive() {
    if (!this.active) {
      return;
    }

    this.#setOpen();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-toc-item': TocItemComponent;
  }
}
